import React, { useMemo, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Download, Activity, CheckCircle, AlertTriangle, ChevronRight, Building2, Layers, TrendingUp } from 'lucide-react';
import { productionRecords, getStageConfig } from '../../data/productionData';
// --- Shared Data & Helpers ---
type OptionMeta = {
  name: string;
  builderName: string;
  builderId: string;
  category: string;
  subcategory: string;
};
export const optionMeta: Record<string, OptionMeta> = {
  'OPT-001': {
    name: 'Black Asphalt Shingles',
    builderName: 'Mattamy Homes',
    builderId: 'mattamy',
    category: 'Exterior',
    subcategory: 'Roofing'
  },
  'OPT-002': {
    name: 'Luxury Vinyl Plank',
    builderName: 'Mattamy Homes',
    builderId: 'mattamy',
    category: 'Interior',
    subcategory: 'Flooring'
  },
  'OPT-003': {
    name: 'Built-in Appliances',
    builderName: 'Schell Brothers',
    builderId: 'schell',
    category: 'Interior',
    subcategory: 'Kitchen'
  },
  'OPT-004': {
    name: 'Engineered Hardwood',
    builderName: 'Schell Brothers',
    builderId: 'schell',
    category: 'Interior',
    subcategory: 'Flooring'
  },
  'OPT-005': {
    name: 'Quartz Countertops',
    builderName: 'Mattamy Homes',
    builderId: 'mattamy',
    category: 'Interior',
    subcategory: 'Kitchen'
  },
  'OPT-006': {
    name: 'Standard Carpet',
    builderName: 'Mattamy Homes',
    builderId: 'mattamy',
    category: 'Interior',
    subcategory: 'Flooring'
  },
  'OPT-007': {
    name: 'Charcoal Asphalt Shingles',
    builderName: 'Excel Homes',
    builderId: 'excel',
    category: 'Exterior',
    subcategory: 'Roofing'
  },
  'OPT-008': {
    name: 'Porcelain Tile',
    builderName: 'Excel Homes',
    builderId: 'excel',
    category: 'Interior',
    subcategory: 'Flooring'
  },
  'OPT-009': {
    name: 'Marble Backsplash',
    builderName: 'Excel Homes',
    builderId: 'excel',
    category: 'Interior',
    subcategory: 'Kitchen'
  },
  'OPT-010': {
    name: 'Granite Countertops',
    builderName: 'Excel Homes',
    builderId: 'excel',
    category: 'Interior',
    subcategory: 'Kitchen'
  },
  'OPT-011': {
    name: 'Cedar Shake Shingles',
    builderName: 'Schell Brothers',
    builderId: 'schell',
    category: 'Exterior',
    subcategory: 'Roofing'
  },
  'OPT-012': {
    name: 'Panel-Ready Dishwasher',
    builderName: 'Schell Brothers',
    builderId: 'schell',
    category: 'Interior',
    subcategory: 'Kitchen'
  },
  'OPT-013': {
    name: 'Slate Tile Roof',
    builderName: 'Schell Brothers',
    builderId: 'schell',
    category: 'Exterior',
    subcategory: 'Roofing'
  },
  'OPT-014': {
    name: 'Scratch-Resistant Laminate',
    builderName: 'Brookfield',
    builderId: 'brookfield',
    category: 'Interior',
    subcategory: 'Flooring'
  },
  'OPT-015': {
    name: 'Bamboo Flooring',
    builderName: 'Brookfield',
    builderId: 'brookfield',
    category: 'Interior',
    subcategory: 'Flooring'
  },
  'OPT-016': {
    name: 'Engineered Hardwood Premium',
    builderName: 'Brookfield',
    builderId: 'brookfield',
    category: 'Interior',
    subcategory: 'Flooring'
  },
  'OPT-017': {
    name: 'Smart Home Package',
    builderName: 'Pulte Homes',
    builderId: 'pulte',
    category: 'Interior',
    subcategory: 'Technology'
  },
  'OPT-018': {
    name: 'Solar Panel Array',
    builderName: 'Pulte Homes',
    builderId: 'pulte',
    category: 'Exterior',
    subcategory: 'Energy'
  },
  'OPT-019': {
    name: 'The Lawson (Model)',
    builderName: 'Mattamy Homes',
    builderId: 'mattamy',
    category: 'Model',
    subcategory: 'Full Model'
  },
  'OPT-020': {
    name: 'The Brookstone (Model)',
    builderName: 'Brookfield',
    builderId: 'brookfield',
    category: 'Model',
    subcategory: 'Full Model'
  },
  'OPT-021': {
    name: 'The Summit (Model)',
    builderName: 'Mattamy Homes',
    builderId: 'mattamy',
    category: 'Model',
    subcategory: 'Full Model'
  },
  'OPT-022': {
    name: 'Exterior Elevations',
    builderName: 'Landmark Homes',
    builderId: 'landmark',
    category: 'Exterior',
    subcategory: 'Elevations'
  },
  'OPT-023': {
    name: 'Premium Flooring Collection',
    builderName: 'Landmark Homes',
    builderId: 'landmark',
    category: 'Interior',
    subcategory: 'Flooring'
  },
  'OPT-024': {
    name: 'Kitchen Cabinetry Package',
    builderName: 'Landmark Homes',
    builderId: 'landmark',
    category: 'Interior',
    subcategory: 'Kitchen'
  },
  'OPT-025': {
    name: 'The Ridgewood (Model)',
    builderName: 'Landmark Homes',
    builderId: 'landmark',
    category: 'Model',
    subcategory: 'Full Model'
  }
};
// ── Generated Option Meta (matching productionData generator) ────────────────
const optionNamePool: Record<string, {
  names: string[];
  categories: [string, string][];
}> = {
  landmark: {
    names: ['Exterior Color Palette A', 'Exterior Color Palette B', 'Stone Veneer Package', 'Brick Accent Options', 'Hardwood Flooring Select', 'Tile Flooring Premium', 'Carpet Collection Standard', 'Vinyl Plank Waterproof', 'Kitchen Island Upgrade', 'Cabinet Door Styles', 'Countertop Quartz Premium', 'Backsplash Mosaic Tile', 'Master Bath Upgrade', 'Ensuite Tile Package', 'Lighting Fixture Package', 'Smart Home Wiring', 'Garage Door Styles', 'Deck & Patio Options', 'Landscaping Package', 'The Ridgewood II (Model)'],
    categories: [['Exterior', 'Elevations'], ['Exterior', 'Elevations'], ['Exterior', 'Cladding'], ['Exterior', 'Cladding'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Bathroom'], ['Interior', 'Bathroom'], ['Interior', 'Fixtures'], ['Interior', 'Technology'], ['Exterior', 'Garage'], ['Exterior', 'Outdoor'], ['Exterior', 'Landscaping'], ['Model', 'Full Model']]
  },
  mattamy: {
    names: ['Cedar Shake Siding', 'Board & Batten Accent', 'Stucco Finish Premium', 'Metal Roof Accent', 'Engineered Oak Flooring', 'Porcelain Tile Large Format', 'Berber Carpet Upgrade', 'Cork Flooring Eco', 'Double Oven Package', 'Pantry Shelving System', 'Granite Island Top', 'Herringbone Backsplash', 'Freestanding Tub Package', 'Walk-in Shower Upgrade', 'Pendant Light Collection', 'Ceiling Fan Package', 'EV Charger Ready', 'Mudroom Built-ins', 'The Ashford (Model)', 'The Kensington (Model)'],
    categories: [['Exterior', 'Siding'], ['Exterior', 'Siding'], ['Exterior', 'Finish'], ['Exterior', 'Roofing'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Bathroom'], ['Interior', 'Bathroom'], ['Interior', 'Fixtures'], ['Interior', 'Fixtures'], ['Interior', 'Technology'], ['Interior', 'Storage'], ['Model', 'Full Model'], ['Model', 'Full Model']]
  },
  schell: {
    names: ['Fiber Cement Siding', 'Cultured Stone Base', 'Aluminum Soffit', 'Standing Seam Metal Roof', 'Wide Plank Hickory', 'Marble Tile Bathroom', 'Frieze Carpet Premium', 'Bamboo Flooring Strand', 'Induction Cooktop', 'Custom Range Hood', 'Butcher Block Island', 'Subway Tile Classic', 'Soaking Tub Freestanding', 'Frameless Glass Shower', 'Recessed Lighting Plan', 'Under Cabinet LED', 'Whole Home Audio', 'Built-in Bookcase', 'The Seabreeze (Model)', 'The Harborview (Model)'],
    categories: [['Exterior', 'Siding'], ['Exterior', 'Cladding'], ['Exterior', 'Trim'], ['Exterior', 'Roofing'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Bathroom'], ['Interior', 'Bathroom'], ['Interior', 'Fixtures'], ['Interior', 'Fixtures'], ['Interior', 'Technology'], ['Interior', 'Storage'], ['Model', 'Full Model'], ['Model', 'Full Model']]
  },
  excel: {
    names: ['Vinyl Siding Premium', 'Natural Stone Accent', 'Composite Trim Package', 'Architectural Shingles', 'Laminate Flooring Select', 'Ceramic Tile Standard', 'Loop Carpet Budget', 'Sheet Vinyl Kitchen', 'Gas Range Upgrade', 'Lazy Susan Corner', 'Laminate Countertop', 'Peel & Stick Backsplash', 'Tub/Shower Combo', 'Pedestal Sink Upgrade', 'Track Lighting Kit', 'Dimmer Switch Package', 'Security System Pre-wire', 'Closet Organizer', 'The Pinnacle (Model)', 'The Crestview (Model)'],
    categories: [['Exterior', 'Siding'], ['Exterior', 'Cladding'], ['Exterior', 'Trim'], ['Exterior', 'Roofing'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Bathroom'], ['Interior', 'Bathroom'], ['Interior', 'Fixtures'], ['Interior', 'Fixtures'], ['Interior', 'Technology'], ['Interior', 'Storage'], ['Model', 'Full Model'], ['Model', 'Full Model']]
  },
  brookfield: {
    names: ['Hardie Plank Siding', 'Limestone Veneer', 'PVC Fascia Board', 'Slate Look Shingles', 'Luxury Vinyl Tile', 'Terrazzo Tile Accent', 'Saxony Carpet Plush', 'Reclaimed Wood Accent', 'French Door Fridge Space', 'Spice Rack Pull-out', 'Solid Surface Counter', 'Glass Tile Accent', 'Rainfall Showerhead', 'Double Vanity Upgrade', 'Chandelier Package', 'Sconce Lighting Set', 'Thermostat Smart Package', 'Window Seat Built-in', 'The Willowbrook (Model)', 'The Stonegate (Model)'],
    categories: [['Exterior', 'Siding'], ['Exterior', 'Cladding'], ['Exterior', 'Trim'], ['Exterior', 'Roofing'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Bathroom'], ['Interior', 'Bathroom'], ['Interior', 'Fixtures'], ['Interior', 'Fixtures'], ['Interior', 'Technology'], ['Interior', 'Storage'], ['Model', 'Full Model'], ['Model', 'Full Model']]
  },
  pulte: {
    names: ['Lap Siding Standard', 'Brick Full Facade', 'Aluminum Gutter System', 'Asphalt Shingle Standard', 'LVP Waterproof Basic', 'Mosaic Tile Shower', 'Textured Carpet Standard', 'Concrete Stain Garage', 'Microwave Built-in', 'Drawer Organizer Set', 'Tile Countertop', 'Painted Backsplash', 'Alcove Tub Standard', 'Linen Closet Shelving', 'Flush Mount Ceiling', 'Bathroom Exhaust Fan', 'Doorbell Camera Ready', 'Coat Closet System', 'The Meridian (Model)', 'The Clearwater (Model)'],
    categories: [['Exterior', 'Siding'], ['Exterior', 'Cladding'], ['Exterior', 'Trim'], ['Exterior', 'Roofing'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Flooring'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Kitchen'], ['Interior', 'Bathroom'], ['Interior', 'Bathroom'], ['Interior', 'Fixtures'], ['Interior', 'Fixtures'], ['Interior', 'Technology'], ['Interior', 'Storage'], ['Model', 'Full Model'], ['Model', 'Full Model']]
  }
};
const builderNameMap: Record<string, string> = {
  landmark: 'Landmark Homes',
  mattamy: 'Mattamy Homes',
  schell: 'Schell Brothers',
  excel: 'Excel Homes',
  brookfield: 'Brookfield',
  pulte: 'Pulte Homes'
};
let _genIdx = 100;
['landmark', 'mattamy', 'schell', 'excel', 'brookfield', 'pulte'].forEach(bid => {
  const pool = optionNamePool[bid];
  if (!pool) return;
  pool.names.forEach((name, i) => {
    const optId = `OPT-${_genIdx}`;
    _genIdx++;
    const [cat, sub] = pool.categories[i] || ['Interior', 'General'];
    optionMeta[optId] = {
      name,
      builderName: builderNameMap[bid],
      builderId: bid,
      category: cat,
      subcategory: sub
    };
  });
});
export const builderColors: Record<string, string> = {
  mattamy: 'bg-indigo-500 text-white',
  schell: 'bg-cyan-500 text-white',
  excel: 'bg-amber-500 text-white',
  brookfield: 'bg-emerald-500 text-white',
  pulte: 'bg-rose-500 text-white',
  landmark: 'bg-teal-600 text-white'
};
const TODAY = new Date('2026-03-14');
export const getDaysInStage = (dateStr: string): number => {
  if (!dateStr) return 0;
  const diffTime = Math.abs(TODAY.getTime() - new Date(dateStr).getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};
export const buildersList = [{
  id: 'landmark',
  name: 'Landmark Homes'
}, {
  id: 'mattamy',
  name: 'Mattamy Homes'
}, {
  id: 'schell',
  name: 'Schell Brothers'
}, {
  id: 'excel',
  name: 'Excel Homes'
}, {
  id: 'brookfield',
  name: 'Brookfield'
}, {
  id: 'pulte',
  name: 'Pulte Homes'
}];
// --- SVG Donut Chart Component ---
export function DonutChart({
  livePct,
  inProgressPct
}: {
  livePct: number;
  inProgressPct: number;
}) {
  const r = 40;
  const cx = 60;
  const cy = 60;
  const circumference = 2 * Math.PI * r;
  const inProgressLength = inProgressPct / 100 * circumference;
  const liveLength = livePct / 100 * circumference;
  return <svg width="120" height="120" viewBox="0 0 120 120" className="transform -rotate-90" data-id="element-1980">
      {/* Background (Not Started) */}
      <circle cx={cx} cy={cy} r={r} fill="transparent" stroke="currentColor" strokeWidth="12" className="text-slate-100" data-id="element-1981" />

      {/* In Progress */}
      {inProgressPct > 0 && <circle cx={cx} cy={cy} r={r} fill="transparent" stroke="currentColor" strokeWidth="12" className="text-blue-500" strokeDasharray={`${inProgressLength} ${circumference}`} strokeDashoffset="0" strokeLinecap="round" data-id="element-1982" />}

      {/* Live */}
      {livePct > 0 && <circle cx={cx} cy={cy} r={r} fill="transparent" stroke="currentColor" strokeWidth="12" className="text-emerald-500" strokeDasharray={`${liveLength} ${circumference}`} strokeDashoffset={-inProgressLength} strokeLinecap="round" data-id="element-1983" />}

      {/* Center Text (rotate back to normal) */}
      <text x="60" y="60" textAnchor="middle" dy=".3em" className="text-2xl font-bold fill-slate-800" transform="rotate(90 60 60)" data-id="element-1984">
        {livePct}%
      </text>
    </svg>;
}
// --- Summary Card Component (Vertical Style) ---
export function SummaryCard({
  label,
  value,
  icon,
  accent,
  highlight
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
  accent?: string;
  highlight?: 'red' | 'green' | 'blue';
}) {
  return <Card className="p-4 flex flex-col items-center justify-center text-center gap-2 shadow-sm" data-id="element-1985">
      <div className="h-6 flex items-center justify-center" data-id="element-1986">
        {icon ? icon : <div className={`w-1 h-5 rounded-full ${accent}`} data-id="element-1987" />}
      </div>
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-1988">
        {label}
      </p>
      <p className={`text-2xl font-bold ${highlight === 'red' ? 'text-red-600' : highlight === 'green' ? 'text-emerald-600' : highlight === 'blue' ? 'text-blue-600' : 'text-slate-900'}`} data-id="element-1989">
        {value}
      </p>
    </Card>;
}
export function AdminProductionPage() {
  const navigate = useNavigate();
  const joinedRecords = useMemo(() => {
    return productionRecords.map(record => {
      const meta = optionMeta[record.optionId] || {
        name: 'Unknown Option',
        builderName: 'Unknown Builder',
        builderId: 'unknown',
        category: 'Unknown',
        subcategory: 'Unknown'
      };
      const stageConfig = getStageConfig(record.productionStatus);
      const daysInStage = getDaysInStage(record.stageEnteredAt);
      return {
        ...record,
        ...meta,
        stageConfig,
        daysInStage
      };
    });
  }, []);
  // Global Stats for the Summary Strip
  const stats = useMemo(() => {
    const s = {
      totalInPipeline: 0,
      onboarding: 0,
      development: 0,
      qa: 0,
      launch: 0,
      blocked: 0,
      live: 0
    };
    joinedRecords.forEach(r => {
      if (r.productionStatus === 'live') {
        s.live++;
      } else if (r.productionStatus !== 'not-started') {
        s.totalInPipeline++;
        if (r.stageConfig.phase === 'onboarding') s.onboarding++;
        if (r.stageConfig.phase === 'development') s.development++;
        if (r.stageConfig.phase === 'qa') s.qa++;
        if (r.stageConfig.phase === 'launch') s.launch++;
      }
      if (r.blockers.length > 0) s.blocked++;
    });
    return s;
  }, [joinedRecords]);
  return <div className="space-y-8 max-w-[1200px] mx-auto" data-id="element-1990">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1991">
        <div data-id="element-1992">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1993">
            Production Overview
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-1994">
            Global pipeline status across all builders
          </p>
        </div>
        <Button variant="outline" leftIcon={<Download className="h-4 w-4" data-id="element-1996" />} data-id="element-1995">
          Export Report
        </Button>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1997">
        <Card className="p-5 flex items-start gap-4" data-id="element-1998">
          <div className="h-11 w-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0" data-id="element-1999">
            <Building2 className="h-5 w-5 text-indigo-600" data-id="element-2000" />
          </div>
          <div data-id="element-2001">
            <p className="text-sm text-slate-500 font-medium" data-id="element-2002">
              Active Builders
            </p>
            <p className="text-2xl font-bold text-slate-900 mt-0.5" data-id="element-2003">
              {buildersList.length}
            </p>
            <p className="text-xs text-emerald-600 mt-1" data-id="element-2004">All in production</p>
          </div>
        </Card>

        <Card className="p-5 flex items-start gap-4" data-id="element-2005">
          <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-2006">
            <Layers className="h-5 w-5 text-blue-600" data-id="element-2007" />
          </div>
          <div data-id="element-2008">
            <p className="text-sm text-slate-500 font-medium" data-id="element-2009">Total Options</p>
            <p className="text-2xl font-bold text-slate-900 mt-0.5" data-id="element-2010">
              {joinedRecords.length}
            </p>
            <p className="text-xs text-blue-600 mt-1" data-id="element-2011">
              {stats.totalInPipeline} in pipeline
            </p>
          </div>
        </Card>

        <Card className="p-5 flex items-start gap-4" data-id="element-2012">
          <div className="h-11 w-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-2013">
            <TrendingUp className="h-5 w-5 text-emerald-600" data-id="element-2014" />
          </div>
          <div data-id="element-2015">
            <p className="text-sm text-slate-500 font-medium" data-id="element-2016">
              Overall Progress
            </p>
            <p className="text-2xl font-bold text-emerald-600 mt-0.5" data-id="element-2017">
              {joinedRecords.length > 0 ? Math.round(stats.live / joinedRecords.length * 100) : 0}
              %
            </p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-2018">
              {stats.live} of {joinedRecords.length} live
            </p>
          </div>
        </Card>

        <Card className="p-5 flex items-start gap-4" data-id="element-2019">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${stats.blocked > 0 ? 'bg-red-50' : 'bg-slate-50'}`} data-id="element-2020">
            <AlertTriangle className={`h-5 w-5 ${stats.blocked > 0 ? 'text-red-500' : 'text-slate-400'}`} data-id="element-2021" />
          </div>
          <div data-id="element-2022">
            <p className="text-sm text-slate-500 font-medium" data-id="element-2023">Blocked</p>
            <p className={`text-2xl font-bold mt-0.5 ${stats.blocked > 0 ? 'text-red-600' : 'text-slate-900'}`} data-id="element-2024">
              {stats.blocked}
            </p>
            <p className={`text-xs mt-1 ${stats.blocked > 0 ? 'text-red-500' : 'text-slate-400'}`} data-id="element-2025">
              {stats.blocked > 0 ? 'Needs attention' : 'All clear'}
            </p>
          </div>
        </Card>
      </div>

      {/* Builder Overview Cards (Grid) */}
      <div data-id="element-2026">
        <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-2027">
          Builder Progress
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2028">
          {buildersList.map(builder => {
          const records = joinedRecords.filter(r => r.builderId === builder.id);
          const total = records.length;
          const live = records.filter(r => r.productionStatus === 'live').length;
          const notStarted = records.filter(r => r.productionStatus === 'not-started').length;
          const inProgress = total - live - notStarted;
          const blocked = records.filter(r => r.blockers.length > 0).length;
          const livePct = total > 0 ? Math.round(live / total * 100) : 0;
          const inProgressPct = total > 0 ? Math.round(inProgress / total * 100) : 0;
          return <Card key={builder.id} onClick={() => navigate(`/admin/production/${builder.id}`)} className="cursor-pointer hover:border-blue-300 hover:shadow-md transition-all group overflow-hidden flex flex-col" data-id="element-2029">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 group-hover:bg-blue-50/30 transition-colors" data-id="element-2030">
                  <div className="flex items-center gap-3" data-id="element-2031">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${builderColors[builder.id] || 'bg-slate-300 text-white'}`} data-id="element-2032">
                      {builder.name.charAt(0)}
                    </div>
                    <h3 className="font-semibold text-lg text-slate-900 group-hover:text-blue-700 transition-colors" data-id="element-2033">
                      {builder.name}
                    </h3>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-blue-500 transition-colors" data-id="element-2034" />
                </div>

                <div className="p-6 flex items-center justify-between flex-1" data-id="element-2035">
                  <div className="flex-1 flex justify-center" data-id="element-2036">
                    <DonutChart livePct={livePct} inProgressPct={inProgressPct} data-id="element-2037" />
                  </div>

                  <div className="flex-1 space-y-4 pl-6 border-l border-slate-100" data-id="element-2038">
                    <div data-id="element-2039">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-2040">
                        Total Options
                      </p>
                      <p className="text-2xl font-bold text-slate-900" data-id="element-2041">
                        {total}
                      </p>
                    </div>

                    <div className="flex items-center gap-4" data-id="element-2042">
                      <div data-id="element-2043">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-2044">
                          In Progress
                        </p>
                        <p className="text-lg font-semibold text-blue-600" data-id="element-2045">
                          {inProgress}
                        </p>
                      </div>
                      <div data-id="element-2046">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-2047">
                          Live
                        </p>
                        <p className="text-lg font-semibold text-emerald-600" data-id="element-2048">
                          {live}
                        </p>
                      </div>
                    </div>

                    {blocked > 0 && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-50 text-red-700 border border-red-100" data-id="element-2049">
                        <AlertTriangle className="h-3.5 w-3.5" data-id="element-2050" />
                        <span className="text-xs font-semibold" data-id="element-2051">
                          {blocked} Blocked
                        </span>
                      </div>}
                  </div>
                </div>
              </Card>;
        })}
        </div>
      </div>
    </div>;
}